import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/pro-light-svg-icons/faLock';
import { music24PlanCapabilities } from 'fym-shared/src/music24/music24Plans';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../components/primitives/SMLButton';
import { SMLUpgradeModal } from '../components/SMLUpgradeModal/SMLUpgradeModal';
import { SMLUpgradeFeature } from '../config/music24PlanCapabilities';
import { planNameMessages } from '../config/music24PlanNames';
import { useMusic24Capabilities } from '../hooks/useMusic24Capabilities';
import { planForSocialDays, SocialPeriodDays } from '../promote/socialPeriod';
import { socialMessages as messages } from './socialMessages';
import { withBrandWord } from '../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet per screen; each part of the screen uses its own slice of it */
import styles from './Social.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PeriodUpgradePromptProps {
  /** The locked period that the person clicked. */
  days: SocialPeriodDays;
}

/**
 * The small upgrade prompt at the period picker (design b2). It names the cheapest plan that
 * keeps the period, and its title uses the days of that plan. "See plans" opens the upgrade
 * modal that exists, which also has the version for a member with no billing right.
 */
const PeriodUpgradePromptComponent: React.FC<PeriodUpgradePromptProps> = ({ days }) => {
  const intl = useIntl();
  const { plan: currentPlan, historyDaysLimit } = useMusic24Capabilities();
  const [isModalOpen, setModalOpen] = useState(false);
  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  const tier = planForSocialDays(days);
  const planName = intl.formatMessage(planNameMessages[tier]);
  const planDays = music24PlanCapabilities[tier].historyDaysLimit;

  return (
    <div className={styles.upgrade} role="status">
      <span className={styles.lockChip}>
        <FontAwesomeIcon icon={faLock} />
        {intl.formatMessage(messages.upgradeChip, { plan: planName })}
      </span>
      <b>{intl.formatMessage(messages.upgradeTitle, { days: planDays })}</b>
      <p>
        {withBrandWord(
          intl.formatMessage(messages.upgradeText, {
            currentPlan: intl.formatMessage(planNameMessages[currentPlan]),
            currentDays: historyDaysLimit,
            plan: planName,
            days: planDays,
          })
        )}
      </p>
      <SMLButton variant="primary" className={styles.upgradeButton} onClick={openModal}>
        {intl.formatMessage(messages.upgradeButton)}
      </SMLButton>
      <SMLUpgradeModal
        isOpen={isModalOpen}
        onClose={closeModal}
        feature={SMLUpgradeFeature.history}
        requiredHistoryDays={days}
      />
    </div>
  );
};

export const PeriodUpgradePrompt = TypedMemo(PeriodUpgradePromptComponent);
